/* eslint-disable sonarjs/cognitive-complexity */
import { cx } from '@emotion/css';
import {
  CardMembershipTwoTone,
  CollectionsTwoTone,
  EmojiEventsTwoTone,
  FlagTwoTone,
  LaptopTwoTone,
  PeopleTwoTone,
  WorkTwoTone,
} from '@mui/icons-material';
import { Rating } from '@sortlist-frontend/design-system/legacy';
import { ClaimedFlag, getFormattedRating, TrustedFlag } from '@sortlist-frontend/shared-components';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import { isNonEmptyString, stringToSafeInteger } from '@sortlist-frontend/utils';

import { ClampLines } from '_components/common/ClampLines';
import { Obfuscate } from '_components/common/Obfuscate/Obfuscate';
import { ComparedAgency } from '_components/Comparator/utils';
import { usePublicAppContext } from '_core/context/public-app-context';
import { SectionBox } from '_features/AgencyProfile/components/SectionBox';
import { SectionsDetails } from '_features/AgencyProfile/components/SectionsDetails';
import { agencyProfileConfig } from '_features/AgencyProfile/config';
import { useGetAgencyProfile } from '_features/AgencyProfile/hooks/useGetAgencyProfile';
import { AgencyStats, Section } from '_features/AgencyProfile/utils';

import { CallToActions } from './CallToActions';
import { Languages } from './Languages';
import * as S from './styles';

type Props = {
  agencyStats: AgencyStats;
  children?: never;
  className?: string;
  handleActivePanel: (value: Section | undefined) => void;
  comparedAgencies: ComparedAgency[];
  setComparedAgencies: (agencies: ComparedAgency[]) => void;
  tagline?: string | null;
  description?: string | null;
};

const getYearFromIso8601 = (date: string): number | null => {
  return stringToSafeInteger(date.slice(0, 4));
};

const getSection = (value: string) => {
  return SectionsDetails.find((section) => section.id === value);
};

export const AboutPanel = ({
  className,
  handleActivePanel,
  agencyStats,
  comparedAgencies,
  setComparedAgencies,
  tagline,
  description,
}: Props) => {
  const { locale, pageData, getUrl } = usePublicAppContext();
  const { agencySlug } = pageData as { agencySlug: string };
  const { data: { agency } = {} } = useGetAgencyProfile({ agencySlug, locale: locale as string });
  const { t } = useTranslation(agencyProfileConfig.i18nNamespaces);

  const {
    name,
    claimed,
    languages,
    remotely,
    foundedAt,
    trustedPartner,
    paidMember,
    createdAt,
    wonOpportunitiesCount: collaborationsCount,
  } = agency ?? {};
  const locality = agency?.mainOffice?.locality?.name;
  const country = agency?.mainOffice?.locality?.country?.name;
  const address = agency?.mainOffice?.address;
  const { teamMembersCount, worksCount, reviewsCount, reviewsScore, awardsCount } = agencyStats;

  if (agency == null) return null;

  return (
    <SectionBox id="about" className={cx(className, 'relative')}>
      <div className="pb-16 max-sm:pt-16 sm:pt-4 lg:px-8 px-0 layout-column layout-gt-sm-row">
        <div className="flex-none flex-gt-sm-70 layout-column p-4">
          <div className="p-2">
            <div className="m-2 layout-column layout-align-start-start">
              <div className="layout-row layout-align-center-center">
                <h1 className="bold h1 text-break-word">{name}</h1>
                <S.FlagsWrapper className="layout-row ml-4">
                  {claimed === true ? <ClaimedFlag isPrimary={true} /> : null}
                  {trustedPartner === true && (
                    <TrustedFlag className={cx(claimed === true && 'ml-1')} isPrimary={true} />
                  )}
                </S.FlagsWrapper>
              </div>
              <span className="p">{locality != null && country != null ? `${locality}, ${country}` : address}</span>
            </div>
          </div>

          <div className="p-4 text-break-word">
            {tagline != null && <div className="bold h4 mb-4" dangerouslySetInnerHTML={{ __html: tagline }}></div>}

            {isNonEmptyString(description) && (
              <ClampLines text={description} className="leading-normal space-y-4" sanitizeCollapsedText={true} />
            )}
          </div>

          <div className="layout-row layout-wrap p-2">
            {teamMembersCount > 0 && (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <a
                role={'button'}
                tabIndex={0}
                onKeyDown={() => handleActivePanel(getSection('team'))}
                onClick={() => handleActivePanel(getSection('team'))}
                className="text-secondary-900 cursor-pointer flex-50 flex-xs-100 layout-row layout-align-start-center">
                <PeopleTwoTone style={{ fontSize: 16 }} className="m-2 text-secondary-500" />
                <span className="small m-2">
                  <Trans
                    i18nKey={'agency:profile.about.details.team'}
                    count={teamMembersCount}
                    values={{ count: teamMembersCount }}>
                    <b>{'{{count}}'}</b> people in their team
                  </Trans>
                </span>
              </a>
            )}

            {languages != null && languages?.length > 0 && <Languages />}

            {worksCount > 0 && (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <a
                tabIndex={0}
                role={'button'}
                onKeyDown={() => handleActivePanel(getSection('portfolio'))}
                onClick={() => handleActivePanel(getSection('portfolio'))}
                className="text-secondary-900 cursor-pointer flex-50 flex-xs-100 layout-row layout-align-start-center">
                <CollectionsTwoTone style={{ fontSize: 16 }} className="m-2 text-secondary-500" />
                <span className="m-2 small text-break-word">
                  <Trans
                    i18nKey={'agency:profile.about.details.works'}
                    count={worksCount}
                    values={{ count: worksCount }}>
                    <b>{'{{count}}'} works</b>
                    in their portfolio
                  </Trans>
                </span>
              </a>
            )}
            {collaborationsCount != null && collaborationsCount > 0 && (
              <div className="flex-50 flex-xs-100 layout-row layout-align-start-center">
                <WorkTwoTone style={{ fontSize: 16 }} className="m-2 text-secondary-500" />
                <span className="m-2 small text-break-word">
                  <Trans
                    i18nKey={'agency:profile.about.details.collaborations'}
                    count={collaborationsCount}
                    values={{ count: collaborationsCount }}>
                    <b>{'{{count}}'} collaborations</b>
                    started on Sortlist
                  </Trans>
                </span>
              </div>
            )}
            {remotely === true && (
              <div className="flex-50 flex-xs-100 layout-row layout-align-start-center">
                <LaptopTwoTone style={{ fontSize: 16 }} className="m-2 text-secondary-500" />
                <span className="m-2 small text-break-word">
                  <Trans i18nKey={'agency:profile.about.details.remote'}>
                    <b>Works remotely</b> across the globe
                  </Trans>
                </span>
              </div>
            )}
            {createdAt != null && getYearFromIso8601(createdAt)?.toString().length === 4 && (
              <div className="flex-50 flex-xs-100 layout-row layout-align-start-center">
                <CardMembershipTwoTone style={{ fontSize: 16 }} className="m-2 text-secondary-500" />
                <span className="m-2 small text-break-word">
                  <Trans
                    i18nKey={'agency:profile.about.details.membership'}
                    values={{ year: getYearFromIso8601(createdAt) }}>
                    Sortlist
                    <b>member since {'{{year}}'}</b>
                  </Trans>
                </span>
              </div>
            )}
            {foundedAt != null && getYearFromIso8601(foundedAt)?.toString().length === 4 && (
              <div className="flex-50 flex-xs-100 layout-row layout-align-start-center">
                <FlagTwoTone style={{ fontSize: 16 }} className="m-2 text-secondary-500" />
                <span className="m-2 small text-break-word">
                  <Trans
                    i18nKey={'agency:profile.about.details.foundation'}
                    values={{ year: getYearFromIso8601(foundedAt) }}>
                    <b>Founded in {'{{year}}'}</b>
                  </Trans>
                </span>
              </div>
            )}
            {awardsCount > 0 && (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <a
                role={'button'}
                tabIndex={0}
                onKeyDown={() => handleActivePanel(getSection('awards'))}
                onClick={() => handleActivePanel(getSection('awards'))}
                className="text-secondary-900 cursor-pointer flex-50 flex-xs-100 layout-row layout-align-start-center">
                <EmojiEventsTwoTone style={{ fontSize: 16 }} className="m-2 text-secondary-500" />
                <span className="m-2 small text-break-word">
                  <Trans
                    i18nKey={'agency:profile.about.details.awards'}
                    count={awardsCount}
                    values={{ count: awardsCount }}>
                    <b>{'{{count}}'} awards</b>
                    conferred
                  </Trans>
                </span>
              </a>
            )}
          </div>
        </div>
        <div className="flex-none flex-gt-sm-30 p-8">
          <S.AboutSectionStickyBox className="layout-column layout-align-start-stretch">
            <div className="border border-secondary-300 rounded-md mb-2">
              <div className="layout-column py-8 px-4 layout-align-center-center text-center">
                {reviewsCount > 0 ? (
                  <span className="mb-2">
                    <span className="h1 bold">{getFormattedRating(reviewsScore)}</span>
                    <span className="h5">/5</span>
                  </span>
                ) : (
                  <div className="pb-4 px-4 layout-column text-center layout-align-center-center small">
                    <span className="mb-1">{t('agency:profile.about.details.noReview')}</span>
                    <Obfuscate
                      className="underline text-primary-500"
                      href={`/agency/${agencySlug}/review`}
                      obfuscate={true}>
                      {t('agency:profile.about.details.reviewFirstCta')}
                    </Obfuscate>
                  </div>
                )}
                <Rating count={5} rate={reviewsCount > 0 ? getFormattedRating(reviewsScore) : 0} direction="column">
                  {reviewsCount > 0 ? (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <a
                      className="text-secondary-900 underline mt-1 cursor-pointer"
                      onClick={() => handleActivePanel(getSection('reviews'))}
                      onKeyDown={() => handleActivePanel(getSection('reviews'))}
                      tabIndex={0}
                      role={'button'}>
                      <Trans i18nKey={'agency:profile.about.details.reviewsCount'} count={reviewsCount}>
                        ({{ count: reviewsCount }} reviews)
                      </Trans>
                    </a>
                  ) : null}
                </Rating>
              </div>
            </div>

            <CallToActions
              agencySlug={agencySlug}
              agency={agency}
              comparedAgencies={comparedAgencies}
              setComparedAgencies={setComparedAgencies}
              certified={paidMember === true}
            />

            {claimed !== true ? (
              <div className="p-4 small">
                <span className="mb-2">{t('agency:profile.about.notManaged')}</span>{' '}
                <Obfuscate
                  className="text-primary-500 underline"
                  href={getUrl('agency.claim.confirm', { slugOrUuid: agencySlug })}
                  obfuscate={true}>
                  {t('agency:profile.about.claimCta')}
                </Obfuscate>
              </div>
            ) : null}
          </S.AboutSectionStickyBox>
        </div>
      </div>
    </SectionBox>
  );
};
